import { Braces, CircleHelp, Smile, Sparkles, UserRound } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { AiWritePopover } from '~/components/pages/ai/placements/preview/AiWritePopover';
import { DynamicFieldMenu } from '~/components/pages/ai/placements/preview/DynamicFieldMenu';
import { EmojiPicker } from '~/components/pages/ai/placements/preview/EmojiPicker';
import {
  AI_TEXT_ACTIONS,
  type AiTextActions,
} from '~/components/pages/ai/placements/preview/useAiTextActions';
import { DropdownMenu, type DropdownMenuItem } from '~/components/ui';
import { formatDynamicFieldToken } from '~/lib/ai/dynamicFields';
import { INSERT_OVER_LIMIT_TEXT, overLimitReducer, type InsertPopup } from '~/lib/ai/aiWriteState';
import { insertAtCursor } from '~/lib/ai/textInsert';

export const DEFAULT_AI_DISCLAIMER_TEXT =
  'Generated using AI. AI can make mistakes, please double check copy before continuing to post.';

const TOOL_BUTTON_CLASS =
  'focus-ring inline-flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent dark:hover:bg-white/5';

function ToolButton({
  ariaLabel,
  buttonRef,
  children,
  disabled,
  expanded,
  onClick,
  testId,
  title,
  tone,
}: {
  ariaLabel: string;
  buttonRef?: RefObject<HTMLButtonElement>;
  children: ReactNode;
  disabled?: boolean;
  expanded?: boolean;
  onClick?: () => void;
  testId: string;
  title?: string;
  tone: 'brand' | 'neutral';
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={ariaLabel}
      aria-expanded={expanded}
      aria-haspopup={expanded === undefined ? undefined : 'dialog'}
      title={title}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={`${TOOL_BUTTON_CLASS} ${
        tone === 'brand' ? 'text-theme-pink-500' : 'text-gray-500 dark:text-gray-400'
      }`}
    >
      {children}
    </button>
  );
}

/** Splices `insert` at the field's last caret (a blurred input keeps it); false when it would pass the limit. */
function insertIntoField(
  fieldId: string,
  text: string,
  onTextChange: (text: string) => void,
  insert: string,
  characterLimit?: number,
): boolean {
  const element = document.getElementById(fieldId) as HTMLInputElement | HTMLTextAreaElement | null;
  const result = insertAtCursor(text, insert, element, characterLimit);

  if (!result) {
    return false;
  }

  onTextChange(result.text);
  requestAnimationFrame(() => {
    element?.focus();
    element?.setSelectionRange(result.caret, result.caret);
  });

  return true;
}

export interface AiFieldChromeProps {
  ai: AiTextActions;
  aiDisabled: boolean;
  /** Why the AI trigger is off; shown as its title. */
  aiDisabledReason?: string;
  bracesButton?: boolean;
  characterLimit?: number;
  children: ReactNode;
  disclaimerText?: string;
  emojiButton?: boolean;
  fieldId: string;
  hasAiHandler: boolean;
  isDisabled: boolean;
  labelText?: string;
  labelTooltip?: string;
  onTextChange: (text: string) => void;
  required?: boolean;
  showDisclaimer?: boolean;
  testId: string;
  text: string;
  tipText?: string;
}

/**
 * The frame the Text Field and Text Area previews share (preview-only, DESIGN §4.4): a label
 * row carrying the `{}`, emoji and AI triggers, the kit field as children, then the
 * character-limit tip and the AI disclaimer. The kit field draws its own error and counter.
 */
export function AiFieldChrome({
  ai,
  aiDisabled,
  aiDisabledReason,
  bracesButton = true,
  characterLimit,
  children,
  disclaimerText = DEFAULT_AI_DISCLAIMER_TEXT,
  emojiButton = true,
  fieldId,
  hasAiHandler,
  isDisabled,
  labelText,
  labelTooltip,
  onTextChange,
  required = false,
  showDisclaimer = true,
  testId,
  text,
  tipText,
}: AiFieldChromeProps) {
  const isAiOff = isDisabled || aiDisabled || ai.isBusy || !hasAiHandler;
  // The {} and emoji popups would open under the AI Write popover, and an Escape there would
  // also drop its unsaved result, so they wait until it closes.
  const isInsertOff = isDisabled || ai.isBusy || ai.isAiWriteOpen;
  const [isBracesOpen, setIsBracesOpen] = useState(false);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [overLimit, dispatchOverLimit] = useReducer(overLimitReducer, null);
  const bracesRef = useRef<HTMLButtonElement>(null);
  const emojiRef = useRef<HTMLButtonElement>(null);
  const aiWrapRef = useRef<HTMLSpanElement>(null);
  // The popover hangs from (and, when focus is lost, returns it to) whichever AI trigger is rendered.
  const aiAnchorRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    aiAnchorRef.current = aiWrapRef.current?.querySelector('button') ?? null;
  });

  // A stale refusal goes when a popup opens or closes, or the text changes (room may be free).
  useEffect(() => {
    dispatchOverLimit({ type: 'clear' });
  }, [isBracesOpen, isEmojiOpen, text.length]);

  useEffect(() => {
    if (isInsertOff) {
      setIsBracesOpen(false);
      setIsEmojiOpen(false);
    }
  }, [isInsertOff]);

  const noticeFor = (popup: InsertPopup) =>
    overLimit?.popup === popup
      ? { key: overLimit.attempt, text: INSERT_OVER_LIMIT_TEXT }
      : undefined;

  const insert = (popup: InsertPopup, value: string, close: () => void) => {
    if (insertIntoField(fieldId, text, onTextChange, value, characterLimit)) {
      close();
    } else {
      dispatchOverLimit({ type: 'refuse', popup });
    }
  };

  const aiItems: DropdownMenuItem[] = [
    ...AI_TEXT_ACTIONS.flatMap((action, index): DropdownMenuItem[] => {
      const item = {
        label: action.label,
        testId: action.id,
        onClick: () => ai.openAction(action.id),
      };
      return index === 0 ? [item, { divider: true }] : [item];
    }),
    ...(ai.activePersonas.length > 0
      ? [
          { divider: true as const },
          ...ai.activePersonas.map((persona) => ({
            label: persona.name,
            testId: `persona-${persona.id}`,
            icon: <UserRound className="size-4" aria-hidden="true" />,
            onClick: () => ai.openPersona(persona),
          })),
        ]
      : []),
  ];

  const aiTriggerTitle = isDisabled
    ? undefined
    : !hasAiHandler
      ? 'AI actions are unavailable until the placement is saved.'
      : (aiDisabledReason ?? 'AI actions are unavailable.');

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between gap-2">
        {labelText ? (
          <div className="flex min-w-0 items-center gap-1.5">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {labelText}
              {required ? <span aria-hidden="true"> *</span> : null}
            </label>
            {labelTooltip ? (
              <span
                role="img"
                tabIndex={0}
                aria-label={labelTooltip}
                title={labelTooltip}
                className="focus-ring inline-flex rounded-full text-gray-400"
              >
                <CircleHelp className="size-4" aria-hidden="true" />
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          {bracesButton ? (
            <ToolButton
              buttonRef={bracesRef}
              ariaLabel="Insert dynamic field"
              expanded={isBracesOpen}
              disabled={isInsertOff}
              onClick={() => {
                setIsEmojiOpen(false);
                setIsBracesOpen((open) => !open);
              }}
              testId={`${testId}-braces`}
              tone="neutral"
            >
              <Braces className="size-4" aria-hidden="true" />
            </ToolButton>
          ) : null}
          {emojiButton ? (
            <ToolButton
              buttonRef={emojiRef}
              ariaLabel="Insert emoji"
              expanded={isEmojiOpen}
              disabled={isInsertOff}
              onClick={() => {
                setIsBracesOpen(false);
                setIsEmojiOpen((open) => !open);
              }}
              testId={`${testId}-emoji`}
              tone="neutral"
            >
              <Smile className="size-4" aria-hidden="true" />
            </ToolButton>
          ) : null}
          <span ref={aiWrapRef} className="inline-flex">
            {ai.isAiWriteOpen ? (
              <ToolButton ariaLabel="Open AI actions" expanded testId={testId} tone="brand">
                <Sparkles className="size-4" aria-hidden="true" />
              </ToolButton>
            ) : isAiOff ? (
              <ToolButton
                ariaLabel="Open AI actions"
                disabled
                testId={`${testId}-disabled`}
                title={aiTriggerTitle}
                tone="brand"
              >
                <Sparkles className="size-4" aria-hidden="true" />
              </ToolButton>
            ) : (
              <DropdownMenu
                testId={testId}
                trigger={{
                  label: 'Open AI actions',
                  content: <Sparkles className="size-4 text-theme-pink-500" aria-hidden="true" />,
                  className: `${TOOL_BUTTON_CLASS} text-theme-pink-500`,
                }}
                items={aiItems}
              />
            )}
          </span>
        </div>
      </div>

      {children}

      {characterLimit && tipText ? (
        <div className="mt-2 rounded-lg bg-gray-50 px-4 py-2 text-center text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          {tipText}
        </div>
      ) : null}

      {showDisclaimer ? (
        <div className="mt-2 flex items-start gap-2">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-theme-pink-500" aria-hidden="true" />
          <p className="flex-1 text-xs text-gray-500 dark:text-gray-400">{disclaimerText}</p>
        </div>
      ) : null}

      <DynamicFieldMenu
        anchorRef={bracesRef}
        open={isBracesOpen}
        onClose={() => setIsBracesOpen(false)}
        notice={noticeFor('dynamic-field')}
        onSearchChange={() => dispatchOverLimit({ type: 'clear' })}
        onSelect={(field) =>
          insert('dynamic-field', formatDynamicFieldToken(field), () => setIsBracesOpen(false))
        }
        testId={`${testId}-dynamic-fields`}
      />
      <EmojiPicker
        anchorRef={emojiRef}
        open={isEmojiOpen}
        onClose={() => setIsEmojiOpen(false)}
        notice={noticeFor('emoji')}
        onSelect={(emoji) => insert('emoji', emoji, () => setIsEmojiOpen(false))}
        testId={`${testId}-emoji-picker`}
      />
      {ai.isAiWriteOpen ? <AiWritePopover ai={ai} anchorRef={aiAnchorRef} testId={testId} /> : null}
    </div>
  );
}
